import React, { useState } from 'react';
import Logo from '../Logo';
import RegistrationsTable from './RegistrationsTable';
import ExternalUsersManager from './ExternalUsersManager';
import ReferenceContactsManager from './ReferenceContactsManager';
import EventContactsManager from './EventContactsManager';
import ContactsSettings from './ContactsSettings';
import ContactEmailSettings from './ContactEmailSettings';
import DocumentsManager from './DocumentsManager';
import LogoManager from './LogoManager';
import AdminsManager from './AdminsManager';
import ChangePassword from './ChangePassword';
import SmtpSettings from './SmtpSettings';
import SupervisorPanel from './SupervisorPanel';
import BackupManager from './BackupManager';
import KioskSettings from './KioskSettings';
import LanguageSettings from './LanguageSettings';
import WelcomeEmailSettings from './WelcomeEmailSettings';
import AutoCloseSettings from './AutoCloseSettings';
import DeviceAuthSettings from './DeviceAuthSettings';
import LoginLogViewer from './LoginLogViewer';
import AdminLanguageSelect from './AdminLanguageSelect';
import { APP_VERSION } from '../../version';
import '../../styles/admin.css';

import { ta } from '../../i18n/adminT';
// Pagine di lavoro quotidiano, sempre visibili in cima alla barra laterale.
// "Contatti" è esclusa per l'operatore: il suo accesso resta limitato
// esattamente a utenti esterni, persone di riferimento e chiusura ingressi
// (vedi anche middleware/auth.js sul server, dove la stessa restrizione è
// applicata davvero, non solo nascosta in interfaccia).
const MAIN_ITEMS = [
  { key: 'registrations', label: 'Registrazioni' },
  { key: 'external-users', label: 'Utenti esterni' },
  { key: 'reference-contacts', label: 'Persone di riferimento' },
  { key: 'event-contacts', label: 'Contatti', hideForOperator: true, requiresFeature: 'fiere' },
];

// Pagine di configurazione, raccolte sotto "Impostazioni" per non affollare
// il menu principale
const SETTINGS_ITEMS = [
  { key: 'documents', label: 'Documenti privacy/sicurezza' },
  { key: 'logo', label: 'Logo personalizzato' },
  { key: 'kiosk', label: 'Pannello utente' },
  { key: 'languages', label: 'Lingue', superOnly: true },
  { key: 'auto-close', label: 'Chiusura automatica ingressi' },
  { key: 'welcome-email', label: 'Email di benvenuto', superOnly: true },
  { key: 'contacts-settings', label: 'Impostazioni contatti', requiresFeature: 'fiere' },
  { key: 'contact-email', label: 'Email contatti', superOnly: true, requiresFeature: 'fiere' },
  { key: 'admins', label: 'Amministratori', superOnly: true },
  { key: 'device-auth', label: 'Dispositivi autorizzati', superOnly: true },
  { key: 'login-log', label: 'Accessi al pannello', superOnly: true },
  { key: 'smtp', label: 'Impostazioni SMTP', superOnly: true },
  { key: 'backup', label: 'Backup', superOnly: true },
  { key: 'account', label: 'Il mio account' },
];

const SETTINGS_KEYS = SETTINGS_ITEMS.map((item) => item.key);

export default function AdminDashboard({ me, onLogout, onLanguageChange, licenseFeatures = [] }) {
  const [section, setSection] = useState(() => new URLSearchParams(window.location.search).get('section') || 'registrations');
  const [settingsOpen, setSettingsOpen] = useState(SETTINGS_KEYS.includes(section));

  // Il supervisor ha un pannello a sé, con l'unica funzione di reset 2FA
  if (me.isSupervisor) {
    return <SupervisorPanel me={me} onLogout={onLogout} onLanguageChange={onLanguageChange} />;
  }

  const goTo = (key) => {
    setSection(key);
    if (SETTINGS_KEYS.includes(key)) setSettingsOpen(true);
  };

  // L'operatore vede solo le tre pagine consentite: niente gruppo
  // Impostazioni, niente "Il mio account" (coerente con quanto permesso
  // anche lato server, vedi middleware/auth.js).
  const isOperator = Boolean(me.isOperator);

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="sidebar-logo"><Logo size={32} /></div>

        {MAIN_ITEMS
          .filter((item) => !item.hideForOperator || !isOperator)
          .filter((item) => !item.requiresFeature || licenseFeatures.includes(item.requiresFeature))
          .map((item) => (
            <button
              key={item.key}
              className={`admin-nav-item ${section === item.key ? 'active' : ''}`}
              onClick={() => goTo(item.key)}
            >
              {ta(item.label)}
            </button>
          ))}

        {!isOperator && (
          <>
            <button
              className="admin-nav-item admin-nav-item--group"
              onClick={() => setSettingsOpen((v) => !v)}
              aria-expanded={settingsOpen}
            >
              <span>{ta('⚙ Impostazioni')}</span>
              <span className="admin-nav-caret">{settingsOpen ? '▾' : '▸'}</span>
            </button>

            {settingsOpen && (
              <div className="admin-nav-subgroup">
                {SETTINGS_ITEMS
                  .filter((item) => !item.superOnly || me.isSuperAdmin)
                  .filter((item) => !item.requiresFeature || licenseFeatures.includes(item.requiresFeature))
                  .map((item) => (
                    <button
                      key={item.key}
                      className={`admin-nav-item admin-nav-item--sub ${section === item.key ? 'active' : ''}`}
                      onClick={() => goTo(item.key)}
                    >
                      {ta(item.label)}
                    </button>
                  ))}
              </div>
            )}
          </>
        )}

        <div className="admin-nav-spacer" />
        <div style={{ color: 'rgba(246,245,241,0.5)', fontSize: '0.78rem', padding: '0 14px 8px', fontFamily: 'var(--font-mono)' }}>
          {me.username}{me.isSuperAdmin ? ta(' · super-admin') : ''}{isOperator ? ta(' · operatore') : ''}
        </div>
        <AdminLanguageSelect value={me.language || 'it'} onChange={onLanguageChange} />
        <button className="admin-nav-item" onClick={onLogout}>{ta('Esci')}</button>
        <div style={{ color: 'rgba(246,245,241,0.3)', fontSize: '0.68rem', padding: '10px 14px 0', fontFamily: 'var(--font-mono)' }}>
          {ta('ExelFlow v')}{APP_VERSION}
          {licenseFeatures.length > 0 && <><br />{ta('Moduli:')} {licenseFeatures.join(', ')}</>}
        </div>
      </aside>

      <main className="admin-main">
        {section === 'registrations' && <RegistrationsTable isSuperAdmin={me.isSuperAdmin} isOperator={isOperator} />}
        {section === 'external-users' && <ExternalUsersManager />}
        {section === 'reference-contacts' && <ReferenceContactsManager />}
        {!isOperator && licenseFeatures.includes('fiere') && section === 'event-contacts' && <EventContactsManager isSuperAdmin={me.isSuperAdmin} />}
        {!isOperator && licenseFeatures.includes('fiere') && section === 'contacts-settings' && <ContactsSettings />}
        {!isOperator && licenseFeatures.includes('fiere') && section === 'contact-email' && me.isSuperAdmin && <ContactEmailSettings />}
        {!isOperator && section === 'documents' && <DocumentsManager />}
        {!isOperator && section === 'logo' && <LogoManager />}
        {!isOperator && section === 'kiosk' && <KioskSettings />}
        {!isOperator && section === 'languages' && me.isSuperAdmin && <LanguageSettings />}
        {!isOperator && section === 'auto-close' && <AutoCloseSettings />}
        {!isOperator && section === 'admins' && me.isSuperAdmin && <AdminsManager currentAdminId={me.username} />}
        {!isOperator && section === 'device-auth' && me.isSuperAdmin && <DeviceAuthSettings />}
        {!isOperator && section === 'login-log' && me.isSuperAdmin && <LoginLogViewer />}
        {!isOperator && section === 'smtp' && me.isSuperAdmin && <SmtpSettings />}
        {!isOperator && section === 'welcome-email' && me.isSuperAdmin && <WelcomeEmailSettings />}
        {!isOperator && section === 'backup' && me.isSuperAdmin && <BackupManager />}
        {!isOperator && section === 'account' && <ChangePassword />}
      </main>
    </div>
  );
}
